import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Loader2, Save, Languages, CheckCircle2, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { useGlobalMap } from '@/services/use-global-map'

export const Route = createFileRoute('/_authenticated/global-harita/')({
  component: GlobalMapPage,
})

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
  { title: 'Küresel Erişim Yönetimi', href: '/global-harita', isActive: true },
]

const localizedStringSchema = z.object({
  tr: z.string().min(1, 'Türkçe alan zorunludur'),
  en: z.string().optional(),
  fr: z.string().optional(),
  es: z.string().optional(),
  ru: z.string().optional(),
  nl: z.string().optional(),
  ar: z.string().optional(),
})

const globalMapSchema = z.object({
  badge: localizedStringSchema,
  title1: localizedStringSchema,
  title2: localizedStringSchema,
  body: localizedStringSchema,
  regionsText: localizedStringSchema,
  logisticsSupportText: localizedStringSchema,
  mapImage: z.string().optional(),
  stats: z.array(z.object({
    value: z.string(),
    label: localizedStringSchema,
  }))
})

type GlobalMapFormValues = z.infer<typeof globalMapSchema>

const LANGUAGES = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'ar', label: 'العربية' },
]

function GlobalMapPage() {
  const { globalMap, isLoading, updateGlobalMap, isUpdating } = useGlobalMap()
  const [isNormalSaving, setIsNormalSaving] = useState(false)
  const [isAISaving, setIsAISaving] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('')

  const form = useForm<GlobalMapFormValues>({
    resolver: zodResolver(globalMapSchema),
    defaultValues: {
      badge: { tr: '' },
      title1: { tr: '' },
      title2: { tr: '' },
      body: { tr: '' },
      regionsText: { tr: '' },
      logisticsSupportText: { tr: '' },
      mapImage: '',
      stats: []
    },
  })

  const { fields: statFields, append: appendStat, remove: removeStat } = useFieldArray({
    control: form.control,
    name: 'stats',
  })

  // Set default values after fetching
  useEffect(() => {
    if (globalMap) {
      form.reset({
        badge: globalMap.badge || { tr: '' },
        title1: globalMap.title1 || { tr: '' },
        title2: globalMap.title2 || { tr: '' },
        body: globalMap.body || { tr: '' },
        regionsText: globalMap.regionsText || { tr: '' },
        logisticsSupportText: globalMap.logisticsSupportText || { tr: '' },
        mapImage: globalMap.mapImage || '',
        stats: globalMap.stats || [],
      })
    }
  }, [globalMap, form])

  const onSubmit = async (values: GlobalMapFormValues) => {
    setIsNormalSaving(true)
    try {
      await updateGlobalMap(values)
    } finally {
      setIsNormalSaving(false)
    }
  }

  const onTranslateAndSave = async () => {
    const values = form.getValues()
    setIsAISaving(true)
    setShowProgress(true)
    setProgress(10)
    setCurrentStep('AI Çeviri servisine bağlanılıyor...')

    const steps = [
      { p: 40, m: 'Sol taraf metinleri çeviriliyor...' },
      { p: 65, m: 'İstatistik etiketleri çeviriliyor...' },
      { p: 85, m: 'Bölgeler ve Lojistik metinleri çeviriliyor...' },
      { p: 98, m: 'Tüm diller kaydediliyor, lütfen bekleyin...' },
    ]

    let currentStepIdx = 0
    const interval = setInterval(() => {
      if (currentStepIdx < steps.length) {
        setProgress(steps[currentStepIdx].p)
        setCurrentStep(steps[currentStepIdx].m)
        currentStepIdx++
      }
    }, 1200)

    try {
      await updateGlobalMap({ ...values, translate: true } as any)
      clearInterval(interval)
      setProgress(100)
      setCurrentStep('Harita bölümü çevrildi ve başarıyla kaydedildi!')
      setTimeout(() => setShowProgress(false), 2000)
    } catch (error) {
      clearInterval(interval)
      setShowProgress(false)
    } finally {
      setIsAISaving(false)
    }
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[80vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Küresel Erişim & Harita</h1>
                <p className="text-muted-foreground text-sm">Alt kısımdaki harita istatistiklerini ve genel ihracat metinlerini düzenleyin.</p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onTranslateAndSave}
                  disabled={isUpdating}
                  className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                >
                  {isAISaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Languages className="mr-2 h-4 w-4" />}
                  AI ile Çevir ve Kaydet
                </Button>
                <Button type="submit" disabled={isUpdating}>
                  {isNormalSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Değişiklikleri Kaydet
                </Button>
              </div>
            </div>

            <Tabs defaultValue="hero" className="w-full">
              <TabsList className="flex flex-wrap h-auto w-full">
                <TabsTrigger value="hero" className="flex-1">Sol Taraf (Metinler)</TabsTrigger>
                <TabsTrigger value="stats" className="flex-1">İstatistikler</TabsTrigger>
                <TabsTrigger value="map" className="flex-1">Harita & Bölgeler</TabsTrigger>
              </TabsList>

              <TabsContent value="hero" className="space-y-4 pt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Genel Metinler</CardTitle>
                    <CardDescription>Haritanın solunda yer alan tanıtıcı başlıklar ve uzun açıklama.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Tabs defaultValue="tr">
                      <TabsList>
                        {LANGUAGES.map(lang => (
                          <TabsTrigger key={lang.code} value={lang.code}>{lang.label}</TabsTrigger>
                        ))}
                      </TabsList>
                      {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                          <FormField
                            control={form.control}
                            name={`badge.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Rozet ({lang.label})</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Örn: KÜRESEL ERİŞİM" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name={`title1.${lang.code}` as any}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Başlık (Beyaz. Örn: Dünyanın)</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`title2.${lang.code}` as any}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Başlık (Altın. Örn: Her Yerine)</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <FormField
                            control={form.control}
                            name={`body.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Detaylı Açıklama ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} placeholder="Dünyanın dört bir yanına uzanan..." rows={3} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="stats" className="space-y-4 pt-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>İstatistikler</CardTitle>
                        <CardDescription>Sol alt kısımda yer alan rakam odaklı istatistik kutuları.</CardDescription>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => appendStat({ value: '', label: { tr: '' } })}>
                        <Plus className="mr-2 h-4 w-4" /> İstatistik Ekle
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {statFields.map((field, index) => (
                      <Card key={field.id}>
                        <CardHeader className="py-3 flex flex-row items-center justify-between bg-muted/50">
                          <span className="font-semibold text-sm">İstatistik {index + 1}</span>
                          <Button type="button" variant="ghost" size="sm" className="h-8 text-destructive" onClick={() => removeStat(index)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </CardHeader>
                        <CardContent className="pt-4 space-y-4">
                          <FormField
                            control={form.control}
                            name={`stats.${index}.value`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Rakam/Değer</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Örn: 25+ veya 350+" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <Tabs defaultValue="tr">
                            <TabsList className="h-8">
                              {LANGUAGES.map(lang => (
                                <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.code.toUpperCase()}</TabsTrigger>
                              ))}
                            </TabsList>
                            {LANGUAGES.map(lang => (
                              <TabsContent key={lang.code} value={lang.code} className="pt-2">
                                <FormField
                                  control={form.control}
                                  name={`stats.${index}.label.${lang.code}` as any}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="sr-only">Etiket ({lang.code})</FormLabel>
                                      <FormControl>
                                        <Input {...field} placeholder={`Alt etiket, örn: Ülkeye İhracat (${lang.label})`} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </TabsContent>
                            ))}
                          </Tabs>
                        </CardContent>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="map" className="space-y-4 pt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Harita Üzeri Metinler (Sağ Taraf)</CardTitle>
                    <CardDescription>Dünya haritası görsellerinin altındaki 2 adet madde (İhracat Bölgeleri, Lojistik).</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Tabs defaultValue="tr">
                      <TabsList>
                        {LANGUAGES.map(lang => (
                          <TabsTrigger key={lang.code} value={lang.code}>{lang.label}</TabsTrigger>
                        ))}
                      </TabsList>
                      {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                          <FormField
                            control={form.control}
                            name={`regionsText.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Tüm Ürünlerin Güvenle İletildiği... ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} rows={2} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`logisticsSupportText.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Lojistik ve Gümrükleme... ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} rows={2} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </form>
        </Form>

        {/* Translation Progress Dialog */}
        <Dialog open={showProgress} onOpenChange={setShowProgress}>
          <DialogContent className="sm:max-w-md [&>button]:hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {progress === 100 ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                )}
                AI Çeviri İşlemi
              </DialogTitle>
              <DialogDescription>
                Harita içerikleriniz diğer dillere çevriliyor. Lütfen kapatmayın.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <Progress value={progress} className="h-2" />
              <p className="text-sm font-medium text-center text-muted-foreground animate-pulse">
                {currentStep}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </Main>
    </>
  )
}
